// Skeletons.jsx

/**
 * Base generic skeleton box with shimmer effect.
 */
export const SkeletonBox = ({ className = '', rounded = 'rounded-md' }) => {
  return (
    <div
      className={`relative overflow-hidden bg-skeleton-base ${rounded} ${className}`}
    >
      <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
    </div>
  );
};

export const SkeletonText = ({
  className = '',
  width = 'w-full',
  height = 'h-5',
}) => {
  return (
    <SkeletonBox
      className={`${width} ${height} ${className}`}
      rounded="rounded-button"
    />
  );
};

export const SkeletonPageHeader = () => {
  return (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <SkeletonText width="w-48" height="h-8" className="mb-2" />
        <SkeletonText width="w-72" height="h-4" />
      </div>
      <div className="flex items-center gap-3">
        <SkeletonBox className="w-24 h-10" rounded="rounded-button" />
        <SkeletonBox className="w-32 h-10" rounded="rounded-button" />
      </div>
    </div>
  );
};

export const SkeletonStats = () => {
  const defaultAccents = [
    { accent: '#0088FF', bg: 'bg-[#F2F9FF]' },
    { accent: '#EEB90B', bg: 'bg-[#FAF6E6]' },
    { accent: '#50AB3D', bg: 'bg-[#EBF4EA]' },
    { accent: '#CB30E0', bg: 'bg-[#FAEBFC]' },
  ];

  return (
    <div className="grid grid-cols-2 gap-3 font-sf-pro-display lg:grid-cols-4">
      {defaultAccents.map((item, i) => (
        <div
          key={i}
          className="relative flex min-h-[88px] w-full flex-col items-start gap-2 overflow-hidden rounded-[12px] bg-white px-3 py-3 text-left shadow-[0_1px_3px_rgba(0,0,0,0.06)] sm:flex-row sm:items-center sm:gap-3 sm:px-4"
        >
          <span
            className="absolute bottom-3 left-0 top-3 w-[4px] rounded-r-full"
            style={{ backgroundColor: item.accent }}
          />
          <span
            className={`ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] sm:ml-0 sm:h-12 sm:w-12 ${item.bg}`}
          >
            <div className="h-5 w-5 sm:h-6 sm:w-6 rounded-md bg-skeleton-base animate-pulse" />
          </span>
          <div className="ml-1 flex min-w-0 flex-1 flex-col gap-1.5 sm:ml-0">
            <SkeletonText width="w-20 sm:w-24" height="h-3.5 sm:h-4" />
            <SkeletonText width="w-10 sm:w-14" height="h-5 sm:h-6" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const SkeletonCard = ({ className = '' }) => {
  return (
    <div
      className={`p-6 premium-surface rounded-card flex flex-col gap-4 ${className}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex gap-3 items-center">
          <SkeletonBox className="w-12 h-12" rounded="rounded-2xl" />
          <div className="flex flex-col gap-2">
            <SkeletonText width="w-32" height="h-5" />
            <SkeletonText width="w-20" height="h-4" />
          </div>
        </div>
        <SkeletonBox className="w-8 h-8" rounded="rounded-full" />
      </div>
      <SkeletonText width="w-full" height="h-4" />
      <SkeletonText width="w-3/4" height="h-4" />
      <div className="flex gap-2 mt-2">
        <SkeletonBox className="w-16 h-6" rounded="rounded-pill" />
        <SkeletonBox className="w-20 h-6" rounded="rounded-pill" />
      </div>
    </div>
  );
};

export const SkeletonTable = ({ rows = 10, columns = 5 }) => {
  return (
    <div className="w-full overflow-hidden">
      {/* Table Header */}
      <div className="flex border-b border-border-default pb-4 mb-4 gap-4 px-4">
        {[...Array(columns)].map((_, i) => (
          <div key={`th-${i}`} className="flex-1">
            <SkeletonText width="w-24" height="h-5" />
          </div>
        ))}
      </div>

      {/* Table Body */}
      <div className="flex flex-col gap-2">
        {[...Array(rows)].map((_, rowIdx) => (
          <div
            key={`tr-${rowIdx}`}
            className="flex items-center gap-4 py-4 px-4 bg-layer-5-card/50 rounded-lg border border-border-default/50"
          >
            {[...Array(columns)].map((_, colIdx) => (
              <div key={`td-${colIdx}`} className="flex-1">
                <SkeletonText
                  width={colIdx === 0 ? 'w-32' : 'w-24'}
                  height="h-4"
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const SkeletonForm = ({ rows = 3 }) => {
  return (
    <div className="flex flex-col gap-5 w-full">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <SkeletonText width="w-32" height="h-4" />
          <SkeletonBox className="w-full h-12" rounded="rounded-input" />
        </div>
      ))}
      <div className="flex justify-end gap-3 mt-2">
        <SkeletonBox className="w-24 h-11" rounded="rounded-button" />
        <SkeletonBox className="w-32 h-11" rounded="rounded-button" />
      </div>
    </div>
  );
};

/**
 * Table loading state matching DashboardTable* row rhythm.
 */
export const TableSkeleton = ({ rows = 10, columns = 5 }) => {
  return (
    <div className="w-full overflow-x-auto custom-scrollbar rounded-[12px] border border-[#EBF4EA] bg-white premium-surface">
      <table className="dashboard-data-table w-full text-left border-separate border-spacing-0 whitespace-nowrap">
        <thead>
          <tr className="h-[48px] sm:h-[54px] bg-[#1A241B]">
            {[...Array(columns)].map((_, i) => (
              <th
                key={`th-${i}`}
                className={`px-4 ${i === 0 ? '!rounded-l-[12px]' : ''} ${i === columns - 1 ? '!rounded-r-[12px]' : ''}`}
              >
                <SkeletonText
                  width="w-20"
                  height="h-3"
                  className="bg-white/20"
                />
              </th>
            ))}
          </tr>
          <tr aria-hidden="true">
            <th
              colSpan={columns}
              className="!h-2 !p-0 !border-0 !bg-transparent"
            />
          </tr>
        </thead>
        <tbody>
          {[...Array(rows)].map((_, r) => (
            <tr key={`sk-row-${r}`} className="h-[48px] sm:h-[56px]">
              {[...Array(columns)].map((_, c) => (
                <td key={`sk-cell-${r}-${c}`} className="px-4">
                  <SkeletonText
                    width={c === 0 ? 'w-28' : 'w-16'}
                    height="h-4"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Skeleton loading state for Calendar (Month, Week, Day views).
 */
export const SkeletonCalendar = ({ view = 'month' }) => {
  if (view === 'month') {
    return (
      <div className="flex flex-col h-full min-h-[420px] sm:min-h-[480px] lg:min-h-[540px] border border-[#EBF4EA] rounded-xl sm:rounded-2xl overflow-hidden bg-white">
        {/* Header */}
        <div className="grid grid-cols-7 border-b border-[#EBF4EA] shrink-0">
          {[...Array(7)].map((_, i) => (
            <div
              key={`th-${i}`}
              className="p-2 sm:p-3 flex justify-center items-center gap-0.5"
            >
              <SkeletonText width="w-8 sm:w-10" height="h-3" />
            </div>
          ))}
        </div>
        {/* Body */}
        <div className="flex-1 grid grid-cols-7 grid-rows-5">
          {[...Array(35)].map((_, i) => (
            <div
              key={`td-${i}`}
              className="border-r border-b border-[#EBF4EA] p-1.5 sm:p-1.5 flex flex-col gap-1 overflow-hidden bg-white"
            >
              <div className="flex justify-between items-start mb-0.5 sm:mb-1">
                <SkeletonBox
                  className="w-5 h-5 sm:w-6 sm:h-6"
                  rounded="rounded-full"
                />
              </div>
              <div className="flex flex-col gap-0.5">
                {i % 3 === 0 && (
                  <SkeletonBox
                    className="w-full h-[14px] sm:h-[18px]"
                    rounded="rounded"
                  />
                )}
                {i % 5 === 0 && (
                  <SkeletonBox
                    className="w-3/4 h-[14px] sm:h-[18px]"
                    rounded="rounded"
                  />
                )}
                {i % 7 === 0 && (
                  <SkeletonBox
                    className="w-1/2 h-[14px] sm:h-[18px]"
                    rounded="rounded"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // For Week / Day views
  const isDay = view === 'day';
  const daysCount = isDay ? 1 : 7;

  return (
    <div className="flex flex-col flex-1 h-full min-h-[420px] sm:min-h-[480px] lg:min-h-[540px] overflow-hidden">
      <div className="overflow-hidden flex-1 flex flex-col border border-[#EBF4EA] rounded-xl sm:rounded-2xl bg-white">
        {/* Header */}
        <div className="flex border-b border-[#EBF4EA] shrink-0">
          <div className="w-[48px] sm:w-[56px] border-r border-[#EBF4EA]" />
          {[...Array(daysCount)].map((_, i) => (
            <div
              key={`th-${i}`}
              className="flex-1 border-r border-[#EBF4EA] px-2 py-2 flex flex-col items-center justify-center gap-1 bg-white"
            >
              <SkeletonText width="w-6 sm:w-8" height="h-2 sm:h-3" />
              <SkeletonBox
                className="w-6 h-6 sm:w-7 sm:h-7"
                rounded="rounded-full"
              />
            </div>
          ))}
        </div>
        {/* Body with time grid */}
        <div className="flex-1 flex overflow-hidden">
          {/* Time axis */}
          <div className="w-[48px] sm:w-[56px] border-r border-[#EBF4EA] flex flex-col bg-white">
            {[...Array(12)].map((_, i) => (
              <div key={`time-${i}`} className="h-[60px] relative">
                <div className="absolute right-2 -top-1.5">
                  <SkeletonText width="w-6" height="h-2" />
                </div>
              </div>
            ))}
          </div>
          {/* Day columns */}
          <div className="flex-1 flex bg-white">
            {[...Array(daysCount)].map((_, i) => (
              <div
                key={`td-${i}`}
                className="flex-1 border-r border-[#EBF4EA] relative min-w-0"
              >
                {[...Array(12)].map((_, j) => (
                  <div
                    key={`grid-${j}`}
                    className="border-t border-[#F0F0F0] h-[60px]"
                  />
                ))}
                {/* Shimmer Event Blocks */}
                {i % 2 === 0 && (
                  <SkeletonBox className="absolute left-[2px] right-[2px] top-[40px] h-[70px] rounded-md opacity-80" />
                )}
                {i % 3 === 0 && (
                  <SkeletonBox className="absolute left-[2px] right-[2px] top-[140px] h-[50px] rounded-md opacity-80" />
                )}
                {i % 5 === 0 && (
                  <SkeletonBox className="absolute left-[2px] right-[2px] top-[260px] h-[90px] rounded-md opacity-80" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Generic Skeleton for lists (e.g. sidebar items, pipelines).
 */
export const SkeletonList = ({ items = 5, className = '' }) => {
  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {[...Array(items)].map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <SkeletonBox className="w-10 h-10 shrink-0" rounded="rounded-full" />
          <div className="flex flex-col gap-1.5 flex-1">
            <SkeletonText width="w-3/4" height="h-4" />
            <SkeletonText width="w-1/2" height="h-3" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Generic Skeleton for inline dropdowns or search inputs.
 */
export const SkeletonDropdown = ({ className = '', height = 'h-10' }) => {
  return (
    <SkeletonBox
      className={`w-full ${height} ${className}`}
      rounded="rounded-input"
    />
  );
};

/**
 * Generic Skeleton for detail side panels or profile views.
 */
export const SkeletonDetailView = () => {
  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-center gap-4">
        <SkeletonBox className="w-16 h-16 shrink-0" rounded="rounded-full" />
        <div className="flex flex-col gap-2 flex-1">
          <SkeletonText width="w-48" height="h-6" />
          <SkeletonText width="w-32" height="h-4" />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <SkeletonText width="w-24" height="h-4" />
        <div className="grid grid-cols-2 gap-4">
          <SkeletonBox className="h-12 w-full" rounded="rounded-md" />
          <SkeletonBox className="h-12 w-full" rounded="rounded-md" />
          <SkeletonBox className="h-12 w-full" rounded="rounded-md" />
          <SkeletonBox className="h-12 w-full" rounded="rounded-md" />
        </div>
      </div>
      <SkeletonForm rows={2} />
    </div>
  );
};
